import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  getCountryCallingCode,
  isSupportedCountry,
} from 'react-phone-number-input';
import { useRouter } from 'next/router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@lemon-io/ui-kit';
import GoogleIcon from '@mui/icons-material/Google';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import mixpanel from 'mixpanel-browser';

import { commonApi } from '@internal/api/common';
import { signUpApi } from '@internal/api/signup';
import Mark from '@internal/components/app/components/mark';
import useLocationCountry from '@internal/components/common/country-resolver/use-location-country';
import { useUtmContext } from '@internal/components/escape-the-matrix/utm-context';
import { extractValidQueries } from '@internal/components/escape-the-matrix/utm-context/utm-context.utils';
import Routes from '@internal/config/routes';
import { SignUpPayload, SignUpResponse } from '@internal/types/api';
import {
  getSafeRedirectPath,
  REDIRECT_QUERY_PARAM,
} from '@internal/utils/safe-redirect';

import { AccountFormValues, accountSchema } from './account-step.schema';
import { FormSelectField, FormTextField } from './form-fields';
import { saveAuthCookie } from './save-auth-cookie';
import { GoogleButtonWrap } from './signup.styled';
import TermsCheckbox from './terms-checkbox';

const nameFieldSx = { flex: 1, width: '100%' } as const;

const GOOGLE_CLIENT_ID =
  process.env.NEXT_PUBLIC_GOOGLE_AUTH_CLIENT_ID ??
  '52869253047-m6m3nv1snn9jvqo0fg630fphr34bg5ov.apps.googleusercontent.com';

const EMAIL_EXISTS_ERROR = 'An account with this email already exists.';

const sanitizePhone = (value: string): string =>
  value.replace(/[^\d\s+]/g, '').replace(/(?!^)\+/g, '');

const LOGIN_URL = `${process.env.APP_LEMON_URL}/auth/login`;

const loginUrlFor = (redirectPath: string | null): string => {
  if (!redirectPath || typeof window === 'undefined') return LOGIN_URL;
  const url = new URL(LOGIN_URL);
  url.searchParams.append(
    REDIRECT_QUERY_PARAM,
    `${window.location.origin}${redirectPath}`
  );
  return url.toString();
};

const friendlyRegisterError = (rawError?: string): string => {
  if (rawError && /exist/i.test(rawError)) {
    return EMAIL_EXISTS_ERROR;
  }
  return "Couldn't create your account. Please try again.";
};

const AccountStep: React.FC<{
  onRegistered: (apiKey: string, qualified: boolean, linkedin: string) => void;
}> = ({ onRegistered }) => {
  const router = useRouter();
  const redirectPath = getSafeRedirectPath(router.query[REDIRECT_QUERY_PARAM]);
  const { getUtmParams } = useUtmContext();
  const { constants } = commonApi.getConstants.useQuery();
  const { signup, signupLoading } = signUpApi.signup.useMutation();
  const { googleAuth, googleAuthLoading } = signUpApi.googleAuth.useMutation();

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);

  const viewedTracked = useRef(false);
  useEffect(() => {
    if (viewedTracked.current) return;
    viewedTracked.current = true;
    mixpanel.track('me-lemon_registration_form_viewed');
  }, []);

  const countryOptions = useMemo(
    () =>
      Object.keys(constants?.countriesData ?? {})
        .sort((a, b) => a.localeCompare(b))
        .map(name => ({ value: name, label: name })),
    [constants]
  );

  const { country: detectedCountry } = useLocationCountry();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<AccountFormValues>({
    resolver: zodResolver(accountSchema),
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      country: '',
      linkedin: '',
      phone: '',
    },
  });

  useEffect(() => {
    if (!registerError) return undefined;
    const emailExistsError = registerError === EMAIL_EXISTS_ERROR;
    const subscription = watch((_, { name }) => {
      if (emailExistsError && name !== 'email') return;
      setRegisterError(null);
    });
    return () => subscription.unsubscribe();
  }, [registerError, watch]);

  const didPrefillCountry = useRef(false);
  useEffect(() => {
    if (didPrefillCountry.current || !detectedCountry || getValues('country')) {
      return;
    }
    if (!countryOptions.some(option => option.value === detectedCountry))
      return;
    setValue('country', detectedCountry, { shouldValidate: true });
    didPrefillCountry.current = true;
  }, [detectedCountry, countryOptions, getValues, setValue]);

  const selectedCountry = watch('country');
  const [firstName, lastName, email, linkedinValue] = watch([
    'firstName',
    'lastName',
    'email',
    'linkedin',
  ]);
  const requiredFieldsFilled = [
    firstName,
    lastName,
    email,
    selectedCountry,
    linkedinValue,
  ].every(value => Boolean(value?.trim()));
  const dialCode = useMemo(() => {
    const isoCode = selectedCountry
      ? constants?.countriesData?.[selectedCountry]?.code
      : undefined;
    if (!isoCode || !isSupportedCountry(isoCode)) return null;
    return `+${getCountryCallingCode(isoCode)}`;
  }, [selectedCountry, constants]);

  const lastDialCode = useRef<string | null>(null);
  useEffect(() => {
    const currentPhone = (getValues('phone') ?? '').trim();
    const isUntouched = !currentPhone || currentPhone === lastDialCode.current;
    if (isUntouched && currentPhone !== (dialCode ?? '')) {
      setValue('phone', dialCode ? `${dialCode} ` : '');
    }
    lastDialCode.current = dialCode;
  }, [dialCode, getValues, setValue]);

  const handleResponse = (response: SignUpResponse, linkedin: string) => {
    if (!response.success) {
      setRegisterError(friendlyRegisterError(response.error));
      return;
    }

    mixpanel.track('me-lemon_registration_form_dev_submitted');
    saveAuthCookie(response.token);
    onRegistered(response.contact.apiKey, response.qualified ?? true, linkedin);
  };

  const attributionParams = () => {
    const {
      partner_id: _partnerId,
      ref_id: _refId,
      ...utmParams
    } = {
      ...extractValidQueries(router.query),
      ...getUtmParams(),
    };
    const siteRef = router.query.site_ref;
    return {
      ...utmParams,
      ...(typeof siteRef === 'string' && { site_ref: siteRef }),
    };
  };

  const handleGoogleCredential = (credential?: string) => {
    if (!credential) {
      setRegisterError('Google sign-in was cancelled. Please try again.');
      return;
    }
    setRegisterError(null);
    const country = getValues('country') || detectedCountry || '';
    googleAuth(
      { googleAuthToken: credential, country, ...attributionParams() },
      {
        onSuccess: response => {
          if (!response.success) {
            setRegisterError(response.error);
            return;
          }
          if (response.mode === 'signup') {
            mixpanel.track('me-lemon_registration_form_dev_submitted');
            saveAuthCookie(response.token);
            onRegistered(response.apiKey, response.qualified ?? true, '');
            return;
          }
          saveAuthCookie(response.ssoDomainToken);
          window.location.assign(redirectPath ?? Routes.GetSetUp);
        },
        onError: () =>
          setRegisterError('Google sign-in failed. Please try again.'),
      }
    );
  };

  const submit = handleSubmit(data => {
    setRegisterError(null);
    const phone = data.phone?.trim() ?? '';
    const isBareDialCode = /^\+\d{1,4}$/.test(phone);
    const payload: SignUpPayload = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      country: data.country,
      ...(phone && !isBareDialCode && { phone }),
      ...attributionParams(),
    };

    signup(payload, {
      onSuccess: response => handleResponse(response, data.linkedin),
      onError: () =>
        setRegisterError("Couldn't create your account. Please try again."),
    });
  });

  return (
    <>
      <Typography variant="h4">Join Lemon.io</Typography>
      <Typography variant="body2" sx={{ mt: 1, color: 'text.primary' }}>
        Get in front of <Mark>funded startups and scale-ups</Mark> across the
        US, Canada, and Europe.
      </Typography>

      <Box sx={{ mt: 3 }}>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <GoogleButtonWrap $loading={googleAuthLoading}>
            <Button
              variant="outlined"
              color="contrast"
              fullWidth
              icon={<GoogleIcon />}
              sx={{ pointerEvents: 'none' }}
            >
              Continue with Google
            </Button>

            {/* Google renders its own fixed-width button that can't be forced
                to full width, so overlay it transparently and stretch it to
                cover the styled button above — it captures the real click. */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                overflow: 'hidden',
                opacity: 0,
                '& > div': {
                  transform: 'scaleX(4)',
                  transformOrigin: 'left center',
                  height: '100%',
                },
              }}
            >
              <GoogleLogin
                onSuccess={credentialResponse =>
                  handleGoogleCredential(credentialResponse.credential)
                }
                onError={() =>
                  setRegisterError('Google sign-in failed. Please try again.')
                }
                text="continue_with"
                theme="outline"
                size="large"
                shape="rectangular"
                logo_alignment="center"
                width="400"
              />
            </Box>
          </GoogleButtonWrap>
        </GoogleOAuthProvider>

        <Divider sx={{ my: 4 }}>
          <Typography variant="body3" sx={{ color: 'text.secondary50' }}>
            or with email
          </Typography>
        </Divider>

        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <FormTextField
              name="firstName"
              control={control}
              label="First name"
              required
              capitalize
              sx={nameFieldSx}
              errorMessage={errors.firstName?.message}
              dataTestId="join-first-name"
            />
            <FormTextField
              name="lastName"
              control={control}
              label="Last name"
              required
              capitalize
              sx={nameFieldSx}
              errorMessage={errors.lastName?.message}
              dataTestId="join-last-name"
            />
          </Stack>
          <FormTextField
            name="email"
            control={control}
            label="Email"
            required
            errorMessage={errors.email?.message}
            dataTestId="join-email"
          />
          <FormSelectField
            name="country"
            control={control}
            label="Country of residence"
            required
            options={countryOptions}
            errorMessage={errors.country?.message}
            dataTestId="join-country"
          />
          <FormTextField
            name="linkedin"
            control={control}
            label="LinkedIn profile (linkedin.com/in/…)"
            required
            errorMessage={errors.linkedin?.message}
            dataTestId="join-linkedin"
          />
          <FormTextField
            name="phone"
            control={control}
            label="Phone number"
            transform={sanitizePhone}
            errorMessage={errors.phone?.message}
            dataTestId="join-phone"
          />

          <TermsCheckbox checked={termsAccepted} onChange={setTermsAccepted} />

          {registerError && (
            <Alert severity="error">
              {registerError}
              {registerError === EMAIL_EXISTS_ERROR && (
                <>
                  {' '}
                  <Link
                    href={loginUrlFor(redirectPath)}
                    color="inherit"
                    underline="always"
                    data-test-id="join-login-link"
                  >
                    Try logging in instead.
                  </Link>
                </>
              )}
            </Alert>
          )}

          <Box>
            <Button
              variant="main"
              color="primary"
              size="large"
              disabled={
                !termsAccepted ||
                !requiredFieldsFilled ||
                Object.keys(errors).length > 0 ||
                signupLoading ||
                Boolean(registerError)
              }
              onClick={() => void submit()}
              sx={{
                width: '100%',
                whiteSpace: 'nowrap',
                ...(signupLoading && {
                  '&.Mui-disabled': { color: 'common.white', opacity: 1 },
                }),
              }}
            >
              {signupLoading ? 'Creating your account' : 'Create account'}
              {signupLoading && (
                <CircularProgress
                  size={16}
                  sx={{ ml: 1.5, color: 'inherit' }}
                />
              )}
            </Button>
          </Box>
        </Stack>
      </Box>
    </>
  );
};

export default AccountStep;
